import React from 'react';


/**
 * One ruler along an edge of the page-layout editor's sheet (KPR-345), in the
 * PAGE's unit: an inch with eighths, or a centimetre with millimetres.
 *
 * The ticks are CSS gradients in physical units (print.css `.page-ruler`), so
 * a 200in custom sheet costs no more than a letter one and a ruler can never
 * drift from the sheet beside it: both are drawn by the browser in the same
 * unit, which is also why anything that scales the canvas scales them
 * together. Only the numbers are elements, one per major tick.
 *
 * Decorative: the sheet's position is read out by the live region while a
 * block is carried and by the inspector's x / y / w / h, so a ruler is hidden
 * from assistive technology rather than read out as a column of numbers.
 */

// What one numbered division, a half-division and a small tick are, per unit.
const cssLength = (value, pageUnit) => `${Number(value) || 0}${pageUnit}`;

const DIVISIONS = {
	in: { major: 1, mid: 0.5, minor: 0.125 },
	mm: { major: 10, mid: 5, minor: 1 },
};

export const rulerDivisions = pageUnit => DIVISIONS[pageUnit === 'mm' ? 'mm' : 'in'];

/** The divisions a ruler numbers: 0, 1, 2 … up to the last whole one on the edge. */
export const rulerNumbers = (length, pageUnit) => {
	const { major } = rulerDivisions(pageUnit);
	const last = Math.floor(length / major + 1e-9);
	return Array.from({ length: last + 1 }, (_, index) => index);
};

export default function PageRuler({ axis, page }) {
	const horizontal = axis === 'x';
	const length = horizontal ? page.width : page.height;
	const { major, mid, minor } = rulerDivisions(page.unit);
	return (
		<div
			className={`page-ruler page-ruler--${axis}`}
			data-testid={`page-ruler-${axis}`}
			aria-hidden="true"
			style={{
				[horizontal ? 'width' : 'height']: cssLength(length, page.unit),
				'--ruler-major': cssLength(major, page.unit),
				'--ruler-mid': cssLength(mid, page.unit),
				'--ruler-minor': cssLength(minor, page.unit),
			}}
		>
			{rulerNumbers(length, page.unit).map(number => (
				<span
					key={number}
					className="page-ruler__number"
					style={{ [horizontal ? 'left' : 'top']: cssLength(number * major, page.unit) }}
				>
					{number * major}
				</span>
			))}
		</div>
	);
}
